/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * EAM-family parameter packs for the metallic 'eam' force field. Each
 * pack carries a `build()` factory that returns a ManyBodyPotential —
 * letting Sutton-Chen (FCC), Finnis-Sinclair (BCC), and any future
 * Daw-Baskes (tabulated) implementations live in one registry.
 *
 * Standard sources:
 *   - FCC metals (Cu, Ag, Au, Ni, Pd, Pt, Al) → Sutton & Chen, Phil.
 *     Mag. Lett. 61 (1990) 139 — analytic 5-parameter (ε, a, c, m, n) form.
 *   - Cu also → Zhou, Johnson & Wadley, Phys. Rev. B 69 (2004) 144113, in
 *     the separate ZHOU_PACKS registry / 'eam-zhou' force field — the
 *     analytic EAM whose melting point lands near experiment, and the
 *     default for copper (see SUTTON_CHEN_CU_PACK for the measurement).
 *   - BCC metals (V, Cr, Fe, Mo, Ta, W) → Finnis & Sinclair, Phil. Mag.
 *     A50 (1984) 45 — polynomial pair (c₀, c₁, c₂) and quadratic density
 *     (β = 0 except Fe and Cr which use β = 1.8), both with naturally
 *     truncating cutoffs (no shifted-force needed).
 *
 * The 'eam' force field `appliesTo` only registered compositions AND
 * metallic crystals (`crystal.metallic` true); everything else falls
 * back to Morse → generic LJ.
 */
import { Vector3 } from 'three';
import { CrystalState } from '../../types.ts';
import { latticeVectors } from '../../crystal/Crystal.ts';
import { Atom } from '../Atom.ts';
import {
  CleriRosatoPotential,
  FinnisSinclairPotential,
  ManyBodyPotential,
  SuttonChenPotential,
  ZhouEamPotential,
  type ZhouEamParams,
} from './PairPotential.ts';

export interface EamPack {
  /** Literature source for the parameter set. */
  source: string;
  /**
   * One sentence for the force-field picker and the agent: what the fit
   * targeted and what that means for the quantity a student is likely to
   * measure (melting point, cohesion). Optional — families with a shared
   * story fall back to `describeEamFamily`.
   */
  note?: string;
  /**
   * True when a measurement showed another family's pack models this metal
   * better, so this one is offered but not the default (see
   * ForceField.demotedFor). Zhou Mo and W melt 700–900 K too high where
   * Finnis–Sinclair is within 100 K.
   */
  demoted?: boolean;
  /** Build the appropriate ManyBodyPotential for this pack. */
  build(): ManyBodyPotential;
  /**
   * Target static cohesive energy of the ideal lattice (eV/atom, positive).
   * When set, the EAM force field evaluates the ideal lattice's energy
   * with the kernel's ACTUAL cutoff and truncation at configure time and
   * scales the potential's energy so it lands here — see
   * `normalizeCohesion`. Needed for Sutton–Chen: its (a/r)^m density tail
   * decays slowly and the shifted-force truncation we apply to it was not
   * part of the original fit, so at our 7 Å cutoff Cu cohesion came out
   * 5 % shallow and at the 5.2 Å a 3×3 cell gets, 12 % shallow — and the
   * melting point moved with it (measured 2026-10-06). Finnis–Sinclair
   * packs have polynomial cutoffs built into the fit and need none.
   */
  cohesionEv?: number;
}

/**
 * Registry keyed by composition. Elemental metals use a single-element
 * key. The build() factory selects the right potential class — SC for
 * FCC, FS for BCC. Adding a metal is one entry per element; adding an
 * alloy is one entry with multiple elements in the params.
 *
 * Per-element notes:
 *  • FCC PE/atom lands ~10–15 % short of experimental cohesion in our
 *    supercells — that's the shifted-force cutoff truncation of the
 *    Sutton-Chen sum at small N. Forces are still exact (eamForceCheck
 *    ≲ 1e-5); dynamics are correct.
 *  • BCC (FS) doesn't have that offset — the polynomial pair/density
 *    cutoffs are built-in zero-crossings, no truncation discontinuity.
 *    Cohesion comes within a few percent of experiment by design.
 *
 * Experimental cohesion / lattice constant for sanity:
 *   FCC: Cu 3.615 −3.50   Ag 4.086 −2.95   Au 4.078 −3.81
 *        Ni 3.524 −4.44   Pd 3.891 −3.91   Pt 3.924 −5.84
 *        Al 4.050 −3.39
 *   BCC: V  3.028 −5.31   Cr 2.884 −4.10   Fe 2.866 −4.28
 *        Mo 3.147 −6.82   Ta 3.303 −8.10   W  3.165 −8.90   (eV/atom)
 */
/**
 * Sutton–Chen Cu. Still offered (force field 'eam', "EAM — Sutton–Chen"),
 * but no longer the default for copper: the Zhou pack below is, because the
 * melting point is what the Melting of Copper lesson is about and SC gets it
 * badly wrong. Measured 2026-10-06 on the same 4×4×4 slab (8 planes, 15 Å
 * vacuum, 2 fs, Q6 on time-averaged positions, two seeds):
 *   Sutton–Chen (cohesion-normalized):  Q6 collapses 900→950 / 950→1000 K
 *   Zhou 2004:                          Q6 collapses 1200→1250 / 1250→1300 K
 *   experiment:                         1358 K
 * SC's own two-phase T_m is only ~1150 K (it was fitted to cohesion and
 * elastic constants, not melting) and our 7 Å truncation of its (a/r)^6
 * tail costs another ~150 K. Zhou's functions are fully decayed by 5 Å, so
 * the result does not move with the kernel cutoff, and the slab result is
 * within ~100 K of experiment — the thin-film depression of an 8-plane slab.
 * Keeping both lets a student run the same sweep twice and see that the
 * melting point is a prediction of the model, not a fact the model was told.
 */
export const SUTTON_CHEN_CU_PACK: EamPack = {
  source: 'Sutton & Chen (1990), FCC Cu',
  note: 'Fitted to cohesive energy, lattice constant and elastic constants; melts about 400 K below the measured 1358 K (collapse 900–1000 K on a thin slab).',
  cohesionEv: 3.5,
  build: () => new SuttonChenPotential({ Cu: { epsilon: 0.012382, a: 3.61, c: 39.432, m: 6, n: 9 } }),
};

/**
 * Zhou, Johnson & Wadley (2004) Cu — Table II of PRB 69, 144113. Verified
 * headless: ideal-lattice minimum at a = 3.615 Å, E = −3.540 eV/atom (the
 * paper's targets), identical at 5.2 / 7 / 10 Å cutoffs, force check 6e-7.
 * No cohesionEv — nothing to renormalize.
 */
export const ZHOU_CU_PACK: EamPack = {
  source: 'Zhou, Johnson & Wadley (2004), FCC Cu',
  note: 'Fitted to cohesive energy, elastic constants, vacancy energy and the equation of state; melts within about 100 K of the measured 1358 K (collapse 1200–1300 K on a thin slab).',
  build: () =>
    new ZhouEamPotential({
      Cu: {
        re: 2.556162,
        fe: 1.554485,
        rhoe: 21.175871,
        rhos: 21.175395,
        alpha: 8.12762,
        beta: 4.334731,
        A: 0.39662,
        B: 0.548085,
        kappa: 0.308782,
        lambda: 0.756515,
        Fn: [-2.170269, -0.263788, 1.088878, -0.817603],
        F: [-2.19, 0, 0.56183, -2.100595],
        eta: 0.31049,
        Fe: -2.186568,
      },
    }),
};

/**
 * Zhou (2004) packs, keyed by composition like EAM_PACKS. Offered as the
 * separate force field 'eam-zhou' so a student can switch between it and the
 * classic pack for the same metal. Fifteen of the paper's sixteen elements
 * (Pb, which has no classic pack here, is the one left out). Every set was
 * validated headless (2026-10-06) against the paper's own fit targets in
 * the element's own structure at a 7 Å cutoff:
 *   FCC Cu Ag Au Ni Pd Pt Al — a₀ within 0.01 %, E_coh exact (3.54, 2.85,
 *     3.93, 4.45, 3.91, 5.77, 3.58 eV), ρ(a₀)/ρe = 1.0000, B within a few %.
 *   BCC Fe Mo Ta W — a₀ within 0.1 %, ρ/ρe = 1.0000, B exact (167, 263, 193,
 *     310 GPa), E_coh 4.29 / 6.81 / 8.09 / 8.76 eV.
 *   HCP Mg Co Ti Zr (experimental c/a) — a₀ within 0.5 %, ρ/ρe 0.996–1.000,
 *     B within 5 %; Zhou's κ = 0.5, λ = 1 cutoff shape for these decays by
 *     ~2.2·re, so results are identical at 7 and 9 Å.
 * V, Cr (BCC) and Zn, Be (HCP) are not in the paper and keep only their
 * classic packs. Add an element from another source the same way and
 * MEASURE its slab ladder before writing its note.
 */
const zhouPack = (el: string, p: ZhouEamParams, note: string, demoted = false): EamPack => ({
  source: `Zhou, Johnson & Wadley (2004), ${el}`,
  note,
  ...(demoted ? { demoted } : {}),
  build: () => new ZhouEamPotential({ [el]: p }),
});

export const ZHOU_PACKS: Record<string, EamPack> = {
  Cu: ZHOU_CU_PACK,
  Ag: zhouPack(
    'Ag',
    {
      re: 2.891814,
      fe: 1.106232,
      rhoe: 14.6041,
      rhos: 14.604144,
      alpha: 9.13201,
      beta: 4.870405,
      A: 0.277758,
      B: 0.419611,
      kappa: 0.33971,
      lambda: 0.750758,
      Fn: [-1.729364, -0.255882, 0.91205, -0.561432],
      F: [-1.75, 0, 0.744561, -1.15065],
      eta: 0.783924,
      Fe: -1.748423,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1200–1300 K against the measured 1235 K.',
  ),
  Au: zhouPack(
    'Au',
    {
      re: 2.885034,
      fe: 1.529021,
      rhoe: 19.991632,
      rhos: 19.991509,
      alpha: 9.516052,
      beta: 5.075228,
      A: 0.229762,
      B: 0.356666,
      kappa: 0.35657,
      lambda: 0.748798,
      Fn: [-2.937772, -0.500288, 1.601954, -0.83553],
      F: [-2.98, 0, 1.706587, -1.134778],
      eta: 1.021095,
      Fe: -2.978815,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1300–1400 K against the measured 1337 K.',
  ),
  Ni: zhouPack(
    'Ni',
    {
      re: 2.488746,
      fe: 2.007018,
      rhoe: 27.562015,
      rhos: 27.562031,
      alpha: 8.383453,
      beta: 4.471175,
      A: 0.429046,
      B: 0.633531,
      kappa: 0.443599,
      lambda: 0.820658,
      Fn: [-2.693513, -0.076445, 0.241442, -2.375626],
      F: [-2.7, 0, 0.26539, -0.152856],
      eta: 0.469,
      Fe: -2.699486,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1500–1600 K, about 150 K below the measured 1728 K.',
  ),
  Pd: zhouPack(
    'Pd',
    {
      re: 2.750897,
      fe: 1.595417,
      rhoe: 21.335246,
      rhos: 21.940073,
      alpha: 8.697397,
      beta: 4.638612,
      A: 0.406763,
      B: 0.59888,
      kappa: 0.397263,
      lambda: 0.754799,
      Fn: [-2.321006, -0.473983, 1.615343, -0.231681],
      F: [-2.36, 0, 1.481742, -1.675615],
      eta: 1.13,
      Fe: -2.352753,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1600–1700 K, about 150 K below the measured 1828 K.',
  ),
  Pt: zhouPack(
    'Pt',
    {
      re: 2.771916,
      fe: 2.336509,
      rhoe: 33.367564,
      rhos: 35.205357,
      alpha: 7.105782,
      beta: 3.78975,
      A: 0.556398,
      B: 0.696037,
      kappa: 0.385255,
      lambda: 0.77051,
      Fn: [-1.455568, -2.149952, 0.528491, 1.222875],
      F: [-4.17, 0, 3.010561, -2.420128],
      eta: 1.45,
      Fe: -4.145597,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1700–1800 K, about 300 K below the measured 2041 K.',
  ),
  Al: zhouPack(
    'Al',
    {
      re: 2.863924,
      fe: 1.403115,
      rhoe: 20.418205,
      rhos: 23.19574,
      alpha: 6.613165,
      beta: 3.527021,
      A: 0.314873,
      B: 0.365551,
      kappa: 0.379846,
      lambda: 0.759692,
      Fn: [-2.807602, -0.301435, 1.258562, -1.247604],
      F: [-2.83, 0, 0.622245, -2.488244],
      eta: 0.785902,
      Fe: -2.824528,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 700–800 K, about 150 K below the measured 933 K (Sutton–Chen aluminium melts at 400–500 K).',
  ),

  // ── BCC ──────────────────────────────────────────────────────────────
  Fe: zhouPack(
    'Fe',
    {
      re: 2.481987,
      fe: 1.885957,
      rhoe: 20.041463,
      rhos: 20.041463,
      alpha: 9.81827,
      beta: 5.236411,
      A: 0.392811,
      B: 0.646243,
      kappa: 0.170306,
      lambda: 0.340613,
      Fn: [-2.534992, -0.059605, 0.193065, -2.282322],
      F: [-2.54, 0, 0.200269, -0.14877],
      eta: 0.39175,
      Fe: -2.539945,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1900–2000 K, about 150 K above the measured 1811 K (Finnis–Sinclair iron fades gradually over 1600–1900 K instead).',
  ),
  Mo: zhouPack(
    'Mo',
    {
      re: 2.7281,
      fe: 2.72371,
      rhoe: 29.354065,
      rhos: 29.354065,
      alpha: 8.393531,
      beta: 4.47655,
      A: 0.708787,
      B: 1.120373,
      kappa: 0.13764,
      lambda: 0.27528,
      Fn: [-3.692913, -0.178812, 0.38045, -3.13365],
      F: [-3.71, 0, 0.875874, 0.776222],
      eta: 0.790879,
      Fe: -3.712093,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 3500–3600 K, about 700 K above the measured 2896 K — offered for comparison, not the default.',
    true,
  ),
  Ta: zhouPack(
    'Ta',
    {
      re: 2.860082,
      fe: 3.086341,
      rhoe: 33.787168,
      rhos: 33.787168,
      alpha: 8.489528,
      beta: 4.527748,
      A: 0.611679,
      B: 1.032101,
      kappa: 0.176977,
      lambda: 0.353954,
      Fn: [-5.103845, -0.405524, 1.112997, -3.585325],
      F: [-5.14, 0, 1.640098, 0.221375],
      eta: 0.848843,
      Fe: -5.141526,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 3100–3200 K, about 100 K below the measured 3290 K.',
  ),
  W: zhouPack(
    'W',
    {
      re: 2.74084,
      fe: 3.48734,
      rhoe: 37.234847,
      rhos: 37.234847,
      alpha: 8.900114,
      beta: 4.746728,
      A: 0.882435,
      B: 1.394592,
      kappa: 0.139209,
      lambda: 0.278417,
      Fn: [-4.946281, -0.148818, 0.365057, -4.432406],
      F: [-4.96, 0, 0.661935, 0.348147],
      eta: -0.582714,
      Fe: -4.961306,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses only at 4500–4600 K, about 900 K above the measured 3695 K, where Finnis–Sinclair tungsten melts at 3700–3800 K — offered for comparison, not the default.',
    true,
  ),

  // ── HCP ──────────────────────────────────────────────────────────────
  Mg: zhouPack(
    'Mg',
    {
      re: 3.196291,
      fe: 0.544323,
      rhoe: 7.1326,
      rhos: 7.1326,
      alpha: 10.228708,
      beta: 5.455311,
      A: 0.137518,
      B: 0.22593,
      kappa: 0.5,
      lambda: 1.0,
      Fn: [-0.896473, -0.044291, 0.162232, -0.68995],
      F: [-0.9, 0, 0.122838, -0.22601],
      eta: 0.431425,
      Fe: -0.899702,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 750–800 K, about 150 K below the measured 923 K.',
  ),
  Co: zhouPack(
    'Co',
    {
      re: 2.505979,
      fe: 1.975299,
      rhoe: 27.206789,
      rhos: 27.206789,
      alpha: 8.679625,
      beta: 4.629134,
      A: 0.421378,
      B: 0.640107,
      kappa: 0.5,
      lambda: 1.0,
      Fn: [-2.541799, -0.219415, 0.733381, -1.589003],
      F: [-2.56, 0, 0.705845, -0.68714],
      eta: 0.694608,
      Fe: -2.559307,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1800–1900 K, about 100 K above the measured 1768 K.',
  ),
  Ti: zhouPack(
    'Ti',
    {
      re: 2.933872,
      fe: 1.8632,
      rhoe: 25.565138,
      rhos: 25.565138,
      alpha: 8.775431,
      beta: 4.68023,
      A: 0.373601,
      B: 0.570968,
      kappa: 0.5,
      lambda: 1.0,
      Fn: [-3.203773, -0.198262, 0.683779, -2.321732],
      F: [-3.22, 0, 0.608587, -0.75071],
      eta: 0.558572,
      Fe: -3.219176,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 1700–1800 K, about 200 K below the measured 1941 K.',
  ),
  Zr: zhouPack(
    'Zr',
    {
      re: 3.199978,
      fe: 2.230909,
      rhoe: 30.879991,
      rhos: 30.879991,
      alpha: 8.55919,
      beta: 4.564902,
      A: 0.424667,
      B: 0.640054,
      kappa: 0.5,
      lambda: 1.0,
      Fn: [-4.485793, -0.293129, 0.990148, -3.202516],
      F: [-4.51, 0, 0.928602, -0.98187],
      eta: 0.597133,
      Fe: -4.509025,
    },
    'Fitted to cohesion, elastic constants, vacancy energy and the equation of state. Measured on a thin slab: order collapses 2100–2200 K against the measured 2128 K.',
  ),
};

export const EAM_PACKS: Record<string, EamPack> = {
  // ── FCC: Sutton & Chen (1990) ──────────────────────────────────────
  Cu: SUTTON_CHEN_CU_PACK,
  Ag: {
    source: 'Sutton & Chen (1990), FCC Ag',
    note: 'Fitted to cohesion, lattice constant and bulk modulus, not melting; on a thin slab order collapses 950–1100 K against the measured 1235 K.',
    cohesionEv: 2.95,
    build: () => new SuttonChenPotential({ Ag: { epsilon: 0.0025415, a: 4.09, c: 144.41, m: 6, n: 12 } }),
  },
  Au: {
    source: 'Sutton & Chen (1990), FCC Au',
    note: 'Fitted to cohesion, lattice constant and bulk modulus, not melting; on a thin slab order collapses 800–1000 K against the measured 1337 K.',
    cohesionEv: 3.81,
    build: () => new SuttonChenPotential({ Au: { epsilon: 0.012793, a: 4.08, c: 34.408, m: 8, n: 10 } }),
  },
  Ni: {
    source: 'Sutton & Chen (1990), FCC Ni',
    note: 'Fitted to cohesion, lattice constant and bulk modulus, not melting; on a thin slab order collapses 1150–1200 K against the measured 1728 K.',
    cohesionEv: 4.44,
    build: () => new SuttonChenPotential({ Ni: { epsilon: 0.015714, a: 3.52, c: 39.756, m: 6, n: 9 } }),
  },
  Pd: {
    source: 'Sutton & Chen (1990), FCC Pd',
    cohesionEv: 3.91,
    build: () => new SuttonChenPotential({ Pd: { epsilon: 0.0041, a: 3.89, c: 108.526, m: 7, n: 12 } }),
  },
  Pt: {
    source: 'Sutton & Chen (1990), FCC Pt',
    cohesionEv: 5.84,
    build: () => new SuttonChenPotential({ Pt: { epsilon: 0.019833, a: 3.92, c: 34.408, m: 8, n: 10 } }),
  },
  Al: {
    source: 'Sutton & Chen (1990), FCC Al',
    note: 'Fitted to cohesion, lattice constant and bulk modulus, not melting; on a thin slab order collapses already at 400–500 K against the measured 933 K.',
    cohesionEv: 3.39,
    build: () => new SuttonChenPotential({ Al: { epsilon: 0.033147, a: 4.05, c: 16.399, m: 6, n: 7 } }),
  },

  // ── BCC: Finnis & Sinclair (1984) ──────────────────────────────────
  // Polynomial pair (c₀, c₁, c₂) and quadratic density; built-in cutoff
  // zeros so no shifted-force is applied. β = 1.8 for Fe and Cr (cubic
  // correction to the density tail); β = 0 for the others in 1984.
  V: {
    source: 'Finnis & Sinclair (1984), BCC V',
    build: () =>
      new FinnisSinclairPotential({
        V: { c: 3.8, d: 3.692767, A: 2.010637, beta: 0, c0: -0.8816318, c1: 1.4907756, c2: -0.3914 },
      }),
  },
  Cr: {
    source: 'Finnis & Sinclair (1984), BCC Cr',
    build: () =>
      new FinnisSinclairPotential({
        Cr: { c: 2.9, d: 3.91572, A: 1.453418, beta: 1.8, c0: 29.1429813, c1: -23.3975027, c2: 4.7578297 },
      }),
  },
  Fe: {
    source: 'Finnis & Sinclair (1984), BCC Fe',
    note: 'Polynomial EAM fitted to cohesion, lattice constant and elastic constants; on a thin slab the order fades gradually between 1600 and 1900 K rather than collapsing (measured 1811 K).',
    build: () =>
      new FinnisSinclairPotential({
        Fe: { c: 3.4, d: 3.699579, A: 1.828905, beta: 1.8, c0: 1.2110601, c1: -0.751084, c2: 0.1287354 },
      }),
  },
  Mo: {
    source: 'Finnis & Sinclair (1984), BCC Mo',
    build: () =>
      new FinnisSinclairPotential({
        Mo: { c: 3.25, d: 4.114825, A: 1.887117, beta: 0, c0: 43.4475218, c1: -31.9332978, c2: 6.0804249 },
      }),
  },
  Ta: {
    source: 'Finnis & Sinclair (1984), BCC Ta',
    build: () =>
      new FinnisSinclairPotential({
        Ta: { c: 4.2, d: 4.07698, A: 2.591061, beta: 0, c0: 1.2157373, c1: 0.0271471, c2: -0.121735 },
      }),
  },
  W: {
    source: 'Finnis & Sinclair (1984), BCC W',
    note: 'Polynomial EAM fitted to cohesion, lattice constant and elastic constants; on a thin slab order collapses 3700–3800 K against the measured 3695 K.',
    build: () =>
      new FinnisSinclairPotential({
        W: { c: 3.25, d: 4.400224, A: 1.896373, beta: 0, c0: 47.1346499, c1: -33.7665655, c2: 6.2541999 },
      }),
  },

  // ── HCP: Cleri & Rosato (1993), TBSMA ──────────────────────────────
  // Exponential form (A, ξ, p, q, r₀), shipped with shifted-ENERGY
  // truncation (not shifted-force — CR's slow density decay q≈2-3
  // makes the shifted-force linear correction over-subtract ρ at NN).
  //
  // HONEST CAVEAT: HCP with this app's default 3×3×3 supercell gives
  // marginal CR results because the non-orthogonal cell with γ=120°
  // caps the minimum-image perpendicular at ~3·a·√3/2 — for typical
  // HCP a≈3 Å this is ~7.8 Å, which after the σ-scaled kernel cutoff
  // (σ = min(3.5, 0.24·minWidth)) leaves only ~3.7 Å for the potential
  // to act over. That barely covers NN; CR's slow exponentials want
  // ~6-7 Å to converge cohesion to within a few percent.
  //
  // Practical guidance for HCP MD:
  //   - Use ≥ 4×4×4 supercells so the kernel cutoff reaches NNN.
  //   - Cohesion at 3×3×3 typically lands 30-50 % short of experimental.
  //   - Forces still validate via eamForceCheck (kernel math is exact).
  //
  // The EAM force field ALSO bumps the kernel's rCutoff to 2.6 for HCP
  // (was 2.0), buying ~30 % more reach at the same σ — enough for CR's
  // slow exponential density to capture both NN and NNN at the default
  // 3×3×3 supercell. ξ values below are scaled from the original CR
  // 1993 table to match experimental cohesion under this short-cutoff
  // regime; r₀, p, q, A are kept at their published values.
  Mg: {
    source: 'Cleri & Rosato (1993), HCP Mg — ξ rescaled for short-cutoff cohesion',
    build: () => new CleriRosatoPotential({ Mg: { A: 0.029, xi: 0.548, p: 11.92, q: 2.245, r0: 3.197 } }),
  },
  Co: {
    source: 'Cleri & Rosato (1993), HCP Co',
    build: () => new CleriRosatoPotential({ Co: { A: 0.095, xi: 1.488, p: 11.604, q: 2.286, r0: 2.4955 } }),
  },
  Zn: {
    source: 'Cleri & Rosato (1993), HCP Zn — ξ rescaled for short-cutoff cohesion',
    build: () => new CleriRosatoPotential({ Zn: { A: 0.1477, xi: 0.65, p: 9.689, q: 4.602, r0: 2.665 } }),
  },
  Ti: {
    source: 'Cleri & Rosato (1993), HCP Ti — ξ rescaled for short-cutoff cohesion',
    build: () => new CleriRosatoPotential({ Ti: { A: 0.073, xi: 1.31, p: 9.253, q: 2.62, r0: 2.951 } }),
  },
  Zr: {
    source: 'Cleri & Rosato (1993), HCP Zr — ξ rescaled for short-cutoff cohesion',
    build: () => new CleriRosatoPotential({ Zr: { A: 0.1934, xi: 1.945, p: 8.25, q: 3.367, r0: 3.1751 } }),
  },
  Be: {
    source: 'CR-style HCP Be — APPROXIMATE (no original CR 1993 fit; ξ tuned to experimental cohesion)',
    build: () => new CleriRosatoPotential({ Be: { A: 0.035, xi: 1.0, p: 12, q: 2.5, r0: 2.225 } }),
  },

  // ── Intermetallics: Sutton-Chen with Rafii-Tabar & Sutton (1991)
  //    mixing rules. Cu-Au covers Cu₃Au (L1₂), Au₃Cu (L1₂), and CuAu
  //    (L1₀) — all share the {Cu, Au} composition; the structure is
  //    determined by the preset's basis, the pair potential is the
  //    same. Same shape for Ni-Al covering NiAl (B2) and Ni₃Al (L1₂).
  //
  //    The pure-elemental endpoints (Cu, Au, Ni, Al) sit in the SC FCC
  //    table above and the SuttonChenPotential constructor synthesizes
  //    the cross-pair (Cu-Au, Ni-Al) via mixing: ε_AB = √(ε_A·ε_B),
  //    arithmetic mean of a, m, n. The embedding F = −c·ε·√ρ stays
  //    per-atom (uses each atom's own ε and c from its element entry).
  //
  //    Caveat: the mixing rule does NOT capture INTERMETALLIC
  //    ORDERING ENERGY — the small energy gain that makes NiAl prefer
  //    the ordered B2 over a random solid solution. NiAl in particular
  //    is strongly ordered (formation enthalpy ~−0.7 eV/atom) and the
  //    mixed SC will underbind it. Cu-Au is less affected (weakly
  //    ordering, formation enthalpy ~−0.1 eV/atom).
  'Au,Cu': {
    source: 'Sutton-Chen Cu + Au with Rafii-Tabar mixing — covers Cu₃Au, Au₃Cu, CuAu',
    build: () =>
      new SuttonChenPotential({
        Cu: { epsilon: 0.012382, a: 3.61, c: 39.432, m: 6, n: 9 },
        Au: { epsilon: 0.012793, a: 4.08, c: 34.408, m: 8, n: 10 },
      }),
  },
  'Al,Ni': {
    source: 'Sutton-Chen Ni + Al with Rafii-Tabar mixing — covers NiAl, Ni₃Al (ordering energy underestimated)',
    build: () =>
      new SuttonChenPotential({
        Ni: { epsilon: 0.015714, a: 3.52, c: 39.756, m: 6, n: 9 },
        Al: { epsilon: 0.033147, a: 4.05, c: 16.399, m: 6, n: 7 },
      }),
  },

  // β-brass (Cu-Zn, B2) — Sutton-Chen with Quantum-SC (Kimura, Qi,
  // Çağın & Goddard 1998) Zn parameters mixed with the standard SC Cu.
  // Same Rafii-Tabar mixing as Cu-Au / Ni-Al; same ordering-energy
  // caveat. Note: pure Zn metal stays on CR (the {Zn} pack above); this
  // {Cu,Zn} composition resolves separately to the alloy SC path.
  'Cu,Zn': {
    source: 'SC Cu + SC Zn (ε re-fit) with mixing — β-brass (ordering caveat)',
    build: () =>
      new SuttonChenPotential({
        Cu: { epsilon: 0.012382, a: 3.61, c: 39.432, m: 6, n: 9 },
        // Zn-SC ε fit so pure-Zn cohesion = −c·ε·√12 + 6ε ≈ −1.35 eV at
        // c=78.41. Used here only for β-brass mixing; pure Zn metal
        // stays on the CR pack above (different composition key {Zn}).
        Zn: { epsilon: 0.005, a: 2.665, c: 78.41, m: 8, n: 11 },
      }),
  },

  // ── Alkali BCC: Cleri-Rosato form, parameters derived from joint
  //    force-balance + cohesion equations at the BCC nearest-neighbor
  //    distance (r₀ = a√3/2). Equilibrium requires V'(r₀) + 2F'(ρ)·ψ'(r₀)
  //    = 0, which with N=8 gives A = (2q/p)·(ξ/√8) = 0.265·ξ (for p=8,
  //    q=3); cohesion −ξ·√8 + 4A = E_exp then closes the system.
  //    Belashchenko / Wang tabulated EAM is the proper source if
  //    quantitative accuracy past ~10 % is needed.
  Li: {
    source: 'CR-style BCC Li — force-balance + cohesion fit at r₀ (no original 1993 fit)',
    build: () => new CleriRosatoPotential({ Li: { A: 0.244, xi: 0.921, p: 8, q: 3, r0: 3.04 } }),
  },
  Na: {
    source: 'CR-style BCC Na — force-balance + cohesion fit at r₀ (no original 1993 fit)',
    build: () => new CleriRosatoPotential({ Na: { A: 0.166, xi: 0.627, p: 8, q: 3, r0: 3.71 } }),
  },
};

/** Composition key for a crystal: sorted unique basis element symbols. */
const compositionKey = (crystal: CrystalState): string =>
  [...new Set(crystal.basis.map((b) => b.element))].sort().join(',');

/** The classic (Sutton–Chen / Finnis–Sinclair / Cleri–Rosato) pack for `crystal`, or null. */
export const eamPackFor = (crystal: CrystalState): EamPack | null => EAM_PACKS[compositionKey(crystal)] ?? null;

/** The Zhou (2004) pack for `crystal`, or null if that family has none for it. */
export const zhouPackFor = (crystal: CrystalState): EamPack | null => ZHOU_PACKS[compositionKey(crystal)] ?? null;

/**
 * Picker/agent text for a pack: which potential this is and why one would
 * choose it. Packs without their own note get their family's story.
 */
export const describeEamPack = (pack: EamPack): { potential: string; note: string } => {
  const potential = pack.build().name;
  if (pack.note) return { potential, note: pack.note };
  const note =
    potential === 'Finnis-Sinclair'
      ? 'Polynomial EAM for BCC metals, fitted to cohesion, lattice constant and elastic constants; cutoffs are built into the fit.'
      : potential === 'Cleri-Rosato'
        ? 'Tight-binding second-moment EAM, fitted to cohesion and elastic constants; the usual choice for HCP metals.'
        : potential === 'Sutton-Chen'
          ? 'Power-law EAM fitted to cohesion, lattice constant and bulk modulus — not to melting; expect melting points to come out low.'
          : `${pack.source}.`;
  return { potential, note };
};

/**
 * Static energy per atom (eV, negative for a bound lattice) of the IDEAL
 * crystal — `crystal.lattice` + `crystal.basis` tiled to infinity — under
 * `potential` with the pair kernel's cutoff `cutoffAng`. Evaluated with the
 * potential's own ψ/φ/F exactly as the kernel does (same shifted-force
 * truncation), so it is the energy the simulation would report for a
 * perfect bulk cell at this cutoff. Vacuum gaps, defects and dopants on the
 * live cell do not enter: this is the reference the fit targeted.
 */
export const idealLatticeEnergyPerAtom = (
  potential: ManyBodyPotential,
  crystal: CrystalState,
  cutoffAng: number,
): number => {
  const [a, b, c] = latticeVectors(crystal.lattice);
  const basis = crystal.basis;
  if (basis.length === 0 || !(cutoffAng > 0)) return NaN;
  const sites = basis.map((s) =>
    new Vector3().addScaledVector(a, s.fx).addScaledVector(b, s.fy).addScaledVector(c, s.fz),
  );
  // Element is all the Sutton–Chen / Finnis–Sinclair functions read off an Atom.
  const probes = basis.map((s) => ({ elementSymbol: s.element }) as unknown as Atom);
  // Enough cells each way to reach the cutoff across the thinnest direction.
  const vol = Math.abs(a.dot(new Vector3().crossVectors(b, c)));
  const widths = [
    vol / new Vector3().crossVectors(b, c).length(),
    vol / new Vector3().crossVectors(c, a).length(),
    vol / new Vector3().crossVectors(a, b).length(),
  ];
  const reach = widths.map((w) => Math.ceil(cutoffAng / w) + 1);
  const rCutSq = cutoffAng * cutoffAng;
  const d = new Vector3();
  let total = 0;
  for (let p = 0; p < basis.length; p++) {
    let rho = 0;
    let pair = 0;
    for (let i = -reach[0]; i <= reach[0]; i++)
      for (let j = -reach[1]; j <= reach[1]; j++)
        for (let k = -reach[2]; k <= reach[2]; k++)
          for (let q = 0; q < basis.length; q++) {
            d.copy(sites[q]).addScaledVector(a, i).addScaledVector(b, j).addScaledVector(c, k).sub(sites[p]);
            const rsq = d.lengthSq();
            if (rsq < 1e-12 || rsq >= rCutSq) continue;
            const dens = potential.densityContribution(probes[p], probes[q], rsq, rCutSq);
            if (dens) rho += dens.psiOnI;
            // φ only — the embedding derivatives do not enter the energy.
            const pf = potential.pairForce(probes[p], probes[q], rsq, rCutSq, 0, 0);
            if (pf) pair += pf.energy;
          }
    total += potential.embedding(probes[p], rho).F + 0.5 * pair;
  }
  return total / basis.length;
};

/**
 * Scale `potential` so the ideal lattice's static energy at the kernel's
 * cutoff equals the pack's target cohesive energy. No-op for packs without
 * a target or potentials that cannot scale. Returns the factor applied (1
 * when nothing was done) so callers can log it.
 */
export const normalizeCohesion = (
  potential: ManyBodyPotential,
  pack: EamPack,
  crystal: CrystalState,
  cutoffAng: number,
): number => {
  if (!pack.cohesionEv || !potential.scaleEnergy) return 1;
  const e = idealLatticeEnergyPerAtom(potential, crystal, cutoffAng);
  if (!Number.isFinite(e) || e >= 0) return 1;
  const factor = pack.cohesionEv / -e;
  // A factor far from 1 means the lattice handed in is not the one the pack
  // describes (wrong lattice constant, wrong structure) — leave it alone
  // rather than hide that behind a rescale.
  if (factor < 0.7 || factor > 1.4) return 1;
  potential.scaleEnergy(factor);
  return factor;
};
